import { useMemo } from 'react';
import type { ColDef, ICellRendererParams, ValueGetterParams } from 'ag-grid-community';
import { useTheme } from 'src/components/context/ThemeContextProvider';
import { managedReportState, type ManagedReportState } from 'src/components/star-grid/hooks/useManageReports';
import { relativeTime } from 'src/components/star-grid/utils/reportStatus';
import { getReadableDateTimeShortFormat } from 'src/utilities/DateTimeUtilities';
import type { PnlReportSummary } from 'src/components/star-grid/starGridTypes';

export const MANAGE_REPORTS_STATE_LABEL: Record<ManagedReportState, string> = {
  active: 'Active',
  deactivated: 'Deactivated',
  deleted: 'Deleted'
};

/**
 * Columns for the Manage Reports grid.
 *
 * `state` and `last changed by` are plain value columns rather than renderers so that sorting,
 * filtering and quick-search all work on what the user actually reads — a status chip rendered from
 * a boolean is invisible to AG Grid's filters.
 */
export const useManageReportsGrid = (actions: { readonly cellRenderer: string }) => {
  const { agGridTheme } = useTheme();
  const theme = useMemo(
    () =>
      agGridTheme.withParams({
        fontSize: 12,
        headerFontSize: 11,
        cellHorizontalPadding: 10,
        borderColor: '#d5dbdb',
        wrapperBorder: true,
        rowBorder: true,
        columnBorder: true,
        headerColumnBorder: true,
        headerRowBorder: true,
        wrapperBorderRadius: 8,
        headerBackgroundColor: '#f2f3f3',
        oddRowBackgroundColor: '#ffffff'
      }),
    [agGridTheme]
  );
  const columns = useMemo<ColDef<PnlReportSummary>[]>(
    () => [
      {
        field: 'report_name',
        headerName: 'Report',
        minWidth: 240,
        flex: 2,
        cellClass: 'sg-manage-name'
      },
      { field: 'pnl_report_id', headerName: 'ID', width: 80, filter: false, suppressHeaderMenuButton: true },
      {
        colId: 'state',
        headerName: 'State',
        width: 140,
        valueGetter: ({ data }: ValueGetterParams<PnlReportSummary>) => (data ? MANAGE_REPORTS_STATE_LABEL[managedReportState(data)] : ''),
        cellClassRules: {
          'sg-manage-state-active': ({ value }) => value === 'Active',
          'sg-manage-state-deactivated': ({ value }) => value === 'Deactivated',
          'sg-manage-state-deleted': ({ value }) => value === 'Deleted'
        }
      },
      {
        colId: 'template',
        headerName: 'Template',
        width: 170,
        // The TEMPLATE, not the deck profile: gp-by-account renders "Deck Detail", so the profile under
        // a "Template" heading would name the wrong thing. Falls back to the profile for the older
        // reports that predate templates and have no definition_key.
        valueGetter: ({ data }: ValueGetterParams<PnlReportSummary>) => data?.definition_key ?? data?.deck_report ?? '—'
      },
      { field: 'created_by', headerName: 'Created By', width: 150, valueGetter: ({ data }) => data?.created_by ?? '—' },
      {
        // Absolute timestamps in the USER'S timezone, via FinSuite's own helper — the table only showed a
        // relative "changed" string, which answers "recently?" but not "when". `ll`-style date plus time
        // and zone, so two reports edited the same day are still distinguishable.
        colId: 'created_at',
        headerName: 'Created',
        width: 200,
        valueGetter: ({ data }: ValueGetterParams<PnlReportSummary>) => (data?.created_at ? getReadableDateTimeShortFormat(data.created_at) : '—')
      },
      {
        colId: 'updated_at',
        headerName: 'Last Updated',
        width: 200,
        valueGetter: ({ data }: ValueGetterParams<PnlReportSummary>) => (data?.updated_at ? getReadableDateTimeShortFormat(data.updated_at) : '—')
      },
      {
        colId: 'changed',
        headerName: 'Last Changed By',
        width: 190,
        valueGetter: ({ data }: ValueGetterParams<PnlReportSummary>) =>
          data?.updated_by ? `${data.updated_by} · ${relativeTime(data.updated_at)}` : '—'
      },
      {
        colId: 'actions',
        headerName: 'Actions',
        width: 200,
        sortable: false,
        filter: false,
        resizable: false,
        suppressHeaderMenuButton: true,
        cellRenderer: actions.cellRenderer,
        cellClass: 'sg-manage-actions'
      }
    ],
    [actions.cellRenderer]
  );
  return { theme, columns };
};

export type ManageReportsActionParams = ICellRendererParams<PnlReportSummary>;
